import moment from 'moment';

interface SchumerBoxRow {
  title: string;
  value: string;
  description: string;
}

interface PdfGenerationOptions {
  schumerBoxData: SchumerBoxRow[];
  titleText?: string;
  filename?: string;
}

export const generateEppDisclosurePdf = async (
  options: PdfGenerationOptions
): Promise<void> => {
  const {
    schumerBoxData,
    titleText = 'Equal Payment Plan Summary',
    filename,
  } = options;

  try {
    console.log('Starting PDF generation process');

    // Function to sanitize text for PDF content
    const sanitizePdfText = (text: string) => {
      if (!text) return '';

      // Handle special characters that might be problematic
      let sanitized = text;

      // Now escape for PDF syntax
      sanitized = sanitized
        .replace(/\\/g, '\\\\')
        .replace(/\(/g, '\\(')
        .replace(/\)/g, '\\)')
        .replace(/'/g, "'");

      return sanitized;
    };

    // Utility function to safely escape text for PDF
    const escapePdfText = (text: string) => {
      if (!text) return '';

      // First sanitize UTF-8 encoding issues
      const cleanText = sanitizePdfText(text);

      // Now encode French accented characters for PDF
      return (
        cleanText
          // French accented characters
          .replace(/é/g, '\\351')
          .replace(/è/g, '\\350')
          .replace(/ê/g, '\\352')
          .replace(/à/g, '\\340')
          .replace(/â/g, '\\342')
          .replace(/ç/g, '\\347')
          .replace(/ô/g, '\\364')
          .replace(/î/g, '\\356')
          .replace(/ï/g, '\\357')
          .replace(/ù/g, '\\371')
          .replace(/û/g, '\\373')
          .replace(/ë/g, '\\353')
          .replace(/œ/g, 'oe')
          .replace(/æ/g, 'ae')
          .replace(/«/g, '"')
          .replace(/»/g, '"')
      );
    };

    // Function to estimate text height needed for content
    const estimateTextHeight = (
      text: string,
      fontSize: number,
      maxWidth: number
    ) => {
      if (!text) return 0;

      // Remove any newlines for consistent calculation
      const processedText = text.replace(/\n/g, ' ');

      // Calculate approximately how many characters can fit on a line
      const charsPerLine = Math.floor(maxWidth / (fontSize * 0.5));

      // Count how many lines we need
      let lineCount = 0;

      processedText.split('\n').forEach((paragraph) => {
        lineCount += Math.max(1, Math.ceil(paragraph.length / charsPerLine));
        lineCount += 0.5;
      });

      return lineCount * (fontSize * 1.5);
    };

    // Define base positions
    const leftMargin = 72;
    const rightMargin = 540;
    const topMargin = 700;
    const bottomMargin = 220;

    // Define title section
    const titleHeight = 30;
    const titleFontSize = 14;
    const titleTopY = topMargin + titleHeight;

    // Define column percentages
    const colWidthPercentages = [0.18, 0.18, 0.64];

    // Calculate actual column widths
    const pageWidth = rightMargin - leftMargin;
    const columnWidths = colWidthPercentages.map((p) => p * pageWidth);

    // Calculate column positions
    const columnX = [
      leftMargin,
      leftMargin + columnWidths[0],
      leftMargin + columnWidths[0] + columnWidths[1],
    ];

    // Base font sizes
    const contentTitleFontSize = 12;
    const valueFontSize = 12;
    const descFontSize = 12;

    // Calculate dynamic row heights for each data item
    const rowHeights = schumerBoxData.map((row) => {
      const titleHeight = estimateTextHeight(
        row.title,
        contentTitleFontSize,
        columnWidths[0] - 10
      );
      const valueHeight = estimateTextHeight(
        row.value,
        valueFontSize,
        columnWidths[1] - 10
      );

      const descText = row.description || '';
      const isLongDesc = descText.length > 200;

      const actualDescFontSize = isLongDesc ? 10 : descFontSize;

      const descHeight = estimateTextHeight(
        descText,
        actualDescFontSize,
        columnWidths[2] - 20
      );

      const contentHeight = Math.max(titleHeight, valueHeight, descHeight);

      let paddingAmount = 30;

      if (descText.length > 500) {
        paddingAmount = 150;
      } else if (descText.length > 300) {
        paddingAmount = 100;
      } else if (descText.length > 200) {
        paddingAmount = 60;
      }

      return contentHeight + paddingAmount;
    });

    // Calculate rows per page based on available space and row heights
    const availableHeight = topMargin - bottomMargin;

    // Group rows by pages
    const pages: number[][] = [];
    let currentPage: number[] = [];
    let currentPageHeight = 0;

    schumerBoxData.forEach((row, index) => {
      if (
        currentPageHeight + rowHeights[index] > availableHeight &&
        currentPage.length > 0
      ) {
        pages.push(currentPage);
        currentPage = [index];
        currentPageHeight = rowHeights[index];
      } else {
        currentPage.push(index);
        currentPageHeight += rowHeights[index];
      }
    });

    if (currentPage.length > 0) {
      pages.push(currentPage);
    }

    const totalPages = pages.length;

    // Dynamic text wrapping based on available width
    const wrapText = (text: string, maxWidth: number, fontSize: number) => {
      if (!text) return [''];

      const processedText = text;

      if (processedText.includes('\n')) {
        return processedText.split('\n').map((line) => line.trim());
      }

      const charsPerLine = Math.floor(maxWidth / (fontSize * 0.6));
      const words = processedText.split(' ');
      const lines = [];
      let currentLine = '';

      words.forEach((word) => {
        if (
          (currentLine + ' ' + word).length > charsPerLine &&
          currentLine.length > 0
        ) {
          lines.push(currentLine);
          currentLine = word;
        } else {
          currentLine =
            currentLine.length === 0 ? word : currentLine + ' ' + word;
        }
      });

      if (currentLine.length > 0) {
        lines.push(currentLine);
      }

      return lines;
    };

    // Start building the PDF catalog
    let pdfContent = `%PDF-1.4
1 0 obj
<</Type /Catalog /Pages 2 0 R>>
endobj
2 0 obj
<</Type /Pages /Kids [`;

    // Add reference to each page in the document
    for (let i = 0; i < totalPages; i++) {
      pdfContent += `${3 + i * 3} 0 R${i < totalPages - 1 ? ' ' : ''}`;
    }

    pdfContent += `] /Count ${totalPages}>>
endobj
`;

    // Create all the page objects first
    for (let i = 0; i < totalPages; i++) {
      pdfContent += `${3 + i * 3} 0 obj
<</Type /Page /Parent 2 0 R /Resources 4 0 R /MediaBox [0 0 612 792] /Contents ${5 + i * 3} 0 R>>
endobj
`;
    }

    // Add shared resource objects with encoding for special characters
    pdfContent += `4 0 obj
<</Font <</F1 ${3 + totalPages * 3} 0 R /F2 ${4 + totalPages * 3} 0 R>> /Encoding /WinAnsiEncoding>>
endobj
${3 + totalPages * 3} 0 obj
<</Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding>>
endobj
${4 + totalPages * 3} 0 obj
<</Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding>>
endobj
`;

    // Generate content for each page with dynamic sizing
    pages.forEach((pageRows, pageIndex) => {
      const rowPositions: number[] = [];
      let currentYPosition = topMargin;

      pageRows.forEach((rowIndex) => {
        rowPositions.push(currentYPosition);
        currentYPosition -= rowHeights[rowIndex];
      });

      pdfContent += `${5 + pageIndex * 3} 0 obj
<</Length 15000>>
stream
BT /F1 1 Tf ET
% Setup for proper text encoding
1 0 0 1 0 0 cm
BT /F1 12 Tf ET

% Draw table - page ${pageIndex + 1}
2 w % Line width
0 0 0 RG % Stroke color (black)
`;

      let tableTopY = topMargin;

      // Draw title row with grey background (only on first page)
      if (pageIndex === 0) {
        pdfContent += `
0.9 0.9 0.9 rg % Fill color (light grey)
${leftMargin} ${topMargin} m
${rightMargin} ${topMargin} l
${rightMargin} ${titleTopY} l
${leftMargin} ${titleTopY} l
f % Fill the rectangle
`;

        pdfContent += `
BT
/F2 ${titleFontSize} Tf % Bold font for title
0 0 0 rg % Text color (black)
${leftMargin + pageWidth / 2 - titleText.length * titleFontSize * 0.3} ${topMargin + titleHeight / 2 - titleFontSize / 2} Td % Center the text horizontally
(${escapePdfText(titleText)}) Tj
ET
`;

        pdfContent += `
0 0 0 RG % Stroke color (black)
${leftMargin} ${topMargin} m
${rightMargin} ${topMargin} l
${rightMargin} ${titleTopY} l
${leftMargin} ${titleTopY} l
${leftMargin} ${topMargin} l
S
`;

        tableTopY = topMargin;
      }

      // Draw outer border for main table
      pdfContent += `
${leftMargin} ${tableTopY} m
${rightMargin} ${tableTopY} l
${rightMargin} ${currentYPosition} l
${leftMargin} ${currentYPosition} l
${leftMargin} ${tableTopY} l
S
`;

      // Draw vertical lines for columns
      for (const colX of columnX) {
        pdfContent += `
${colX} ${tableTopY} m
${colX} ${currentYPosition} l
S
`;
      }
      pdfContent += `
${rightMargin} ${tableTopY} m
${rightMargin} ${currentYPosition} l
S
`;

      // Draw horizontal lines for rows
      pageRows.forEach((rowIndex, idx) => {
        if (idx > 0) {
          const y = rowPositions[idx];
          pdfContent += `
${leftMargin} ${y} m
${rightMargin} ${y} l
S
`;
        }
      });

      // Add text for each row with dynamic font sizes and wrapping
      pageRows.forEach((rowIndex, idx) => {
        const row = schumerBoxData[rowIndex];
        const topY = rowPositions[idx];
        const bottomY =
          idx < pageRows.length - 1 ? rowPositions[idx + 1] : currentYPosition;

        const cellHeight = topY - bottomY;

        const isLongDesc = row.description?.length > 500;
        const titleActualFontSize = contentTitleFontSize;
        const valueActualFontSize = valueFontSize;
        const descActualFontSize = isLongDesc ? 10 : descFontSize;

        // Add title text (left column)
        const titleLines = wrapText(
          row.title,
          columnWidths[0] - 14,
          titleActualFontSize
        );

        if (titleLines.length > 0) {
          const titleLineHeight = titleActualFontSize * 1.2;
          const titleStartY = topY - 15;

          pdfContent += `
BT
/F1 ${titleActualFontSize} Tf
${columnX[0] + 7} ${titleStartY} Td
`;

          titleLines.forEach((line, lineIdx) => {
            if (lineIdx > 0) {
              pdfContent += `0 -${titleLineHeight} Td\n`;
            }
            const fixedLine = line.replace(/'/g, "'");
            pdfContent += `(${escapePdfText(fixedLine)}) Tj\n`;
          });

          pdfContent += `ET
`;
        }

        // Add value text (middle column)
        const valueLines = wrapText(
          row.value,
          columnWidths[1] - 14,
          valueActualFontSize
        );

        if (valueLines.length > 0) {
          const valueLineHeight = valueActualFontSize * 1.2;
          const valueStartY = topY - 15;

          pdfContent += `
BT
/F1 ${valueActualFontSize} Tf
${columnX[1] + 7} ${valueStartY} Td
`;

          valueLines.forEach((line, lineIdx) => {
            if (lineIdx > 0) {
              pdfContent += `0 -${valueLineHeight} Td\n`;
            }
            const fixedLine = line.replace(/'/g, "'");
            pdfContent += `(${escapePdfText(fixedLine)}) Tj\n`;
          });

          pdfContent += `ET
`;
        }

        // Add description text (right column)
        let descLines;

        if (isLongDesc) {
          let enhancedDesc = row.description || '';

          if (enhancedDesc.includes('\n')) {
            descLines = enhancedDesc
              .split('\n')
              .flatMap((line) =>
                wrapText(line, columnWidths[2] - 20, descActualFontSize)
              );
          } else {
            enhancedDesc = enhancedDesc
              .replace(/\. /g, '.\n')
              .replace(/; /g, ';\n');

            descLines = enhancedDesc
              .split('\n')
              .flatMap((line) =>
                wrapText(line, columnWidths[2] - 20, descActualFontSize)
              );
          }
        } else {
          if (row.description && row.description.includes('\n')) {
            descLines = row.description
              .split('\n')
              .flatMap((line) =>
                wrapText(line, columnWidths[2] - 20, descActualFontSize)
              );
          } else {
            descLines = wrapText(
              row.description,
              columnWidths[2] - 20,
              descActualFontSize
            );
          }
        }

        if (descLines.length > 0) {
          const descLineHeight = descActualFontSize * 1.2;
          const descStartY = topY - 15;

          pdfContent += `
BT
/F1 ${descActualFontSize} Tf
${columnX[2] + 10} ${descStartY} Td
`;

          descLines.forEach((line, lineIdx) => {
            if (lineIdx > 0) {
              pdfContent += `0 -${descLineHeight} Td\n`;
            }
            const fixedLine = line.replace(/'/g, "'");
            pdfContent += `(${escapePdfText(fixedLine)}) Tj\n`;
          });

          pdfContent += `ET
`;
        }
      });

      pdfContent += `
endstream
endobj
`;
    });

    // Close the PDF
    pdfContent += `
trailer
<</Size ${5 + totalPages * 3 + 2} /Root 1 0 R>>
startxref
0
%%EOF`;

    // Create and download PDF file
    const timestamp = moment().format('YYYYMMDDHHmmss');
    const pdfFilename = filename || `EPP_Summary_${timestamp}.pdf`;

    // Convert PDF content to blob and create download link
    const blob = new Blob([pdfContent], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);

    // Create a temporary anchor element and trigger download
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = pdfFilename;
    document.body.appendChild(a);
    a.click();

    // Clean up
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);

    console.log('PDF download initiated successfully');
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw new Error('Unable to generate PDF. Please try again later.');
  }
};
